import type { InputHTMLAttributes, ReactNode } from "react";

import { LAUNCH_OS_ICON } from "@/brand";
import "./authCard.css";

// The shared look of the account screens (sign in, forgot/reset password, setup, hand-off): the
// Launch OS rocket, a short headline, labeled fields, one strong button. Same look as the Claude
// connect page. Light and dark follow the app's own palette (authCard.css); brand.css swaps the
// rocket for its dark version in dark mode. Only looks live here; each screen keeps its own logic.

const PersonGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="12" cy="8.5" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.7" /><path d="M5 19.5c.8-3.3 3.6-5 7-5s6.2 1.7 7 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
);

// Same structure as the Claude connect page: "Launch OS" row above the card; inside, you ---- Launch OS, headline, short line.
export const AuthCard = ({ title, subtitle, children }: { title: string; subtitle?: string; children?: ReactNode }) => (
  <div className="ac-page">
    <div className="ac-wrap">
      <div className="ac-brand">
        <img src={LAUNCH_OS_ICON} alt="" width={28} height={28} />
        <span>Launch OS</span>
      </div>
      <div className="ac-card" data-testid="AuthCard">
        <div className="ac-pair" aria-hidden="false">
          <div className="ac-who"><PersonGlyph /></div>
          <svg viewBox="0 0 24 8" width="40" height="8" aria-hidden="true"><path d="M1 4h22" stroke="currentColor" strokeWidth="2" strokeDasharray="2 4" strokeLinecap="round" /></svg>
          <img className="ac-logo" src={LAUNCH_OS_ICON} alt="Launch OS" width={44} height={44} />
        </div>
        <h1 className="ac-title">{title}</h1>
        {subtitle && <p className="ac-sub">{subtitle}</p>}
        {children}
      </div>
      <div className="ac-foot">Launch OS</div>
    </div>
  </div>
);

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> & {
  label: string;
  aside?: ReactNode; // a link beside the label, like "Forgot password?"
  onChange: (value: string) => void;
};
export const AuthField = ({ label, aside, onChange, id, ...input }: FieldProps) => {
  const fieldId = id || `ac-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="ac-field">
      <div className="ac-labelrow">
        <label htmlFor={fieldId}>{label}</label>
        {aside}
      </div>
      <input id={fieldId} className="ac-input" onChange={(e) => onChange(e.target.value)} {...input} />
    </div>
  );
};

export const AuthButton = ({ busy = false, busyLabel, onClick, children, dataTestId }: { busy?: boolean; busyLabel?: string; onClick?: () => void; children: ReactNode; dataTestId?: string }) => (
  <button type="submit" className="ac-btn" disabled={busy} onClick={onClick} data-testid={dataTestId}>
    {busy && busyLabel ? busyLabel : children}
  </button>
);

export const AuthNotice = ({ tone = "error", children }: { tone?: "error" | "info"; children: ReactNode }) => (
  <div className={`ac-notice ${tone}`} role={tone === "error" ? "alert" : "status"}>{children}</div>
);

// a quiet link under the card body ("Back to sign in")
export const AuthLinkRow = ({ children }: { children: ReactNode }) => <p className="ac-linkrow">{children}</p>;
